import { NextPage } from 'next';
import React from 'react';
import { AnalyticsDataPageScreenType } from 'src/types/googleAnalyticsEvents';
import { FullPageError, TrackedPage } from 'src/components';
import { MemrisePageContext } from 'src/utils/context';
import { convertToMemriseError } from 'src/utils/errors';
import { NotFoundErrorPage } from 'src/containers';
import { getProviderServerSideProps } from 'src/utils/getProviderServerSideProps';
import { getLanguagePair } from 'src/utils/getLanguagePair';
import { getIsLocalizedPath } from 'src/utils/getPathLocale';
import { LanguagePairProvider } from 'src/context/languagePair';
import { TargetLanguagesProvider } from 'src/context/targetLanguages';
import { DetailedSourceLanguageResponse } from 'src/types/languagesDomain';

export const DEFAULT_LANGUAGE_PAIR = {
  sourceLanguageLocale: 'en-us',
  targetLanguageLocale: 'es',
} as DetailedSourceLanguageResponse;

export const STATUS_CODE_NOT_FOUND = 404;

type MemriseErrorPageProps = {
  message?: string | null;
  statusCode?: number;
  languagePair?: DetailedSourceLanguageResponse;
  targetLanguages?: DetailedSourceLanguageResponse[];
};

/**
 * Overwrites the default error page to style it with the memrise header, etc
 */
const MemriseErrorPage: NextPage<MemriseErrorPageProps> = ({
  message,
  languagePair: optionalLanguagePair,
  targetLanguages,
  statusCode,
}) => {
  const languagePair = optionalLanguagePair || DEFAULT_LANGUAGE_PAIR;

  return (
    <LanguagePairProvider languagePair={languagePair}>
      <TrackedPage
        pageName={typeof statusCode === 'number' ? String(statusCode) : 'Unknown status code'}
        pageType={AnalyticsDataPageScreenType.errorPage}
      >
        {statusCode === STATUS_CODE_NOT_FOUND && targetLanguages ? (
          <TargetLanguagesProvider targetLanguages={targetLanguages}>
            <NotFoundErrorPage />
          </TargetLanguagesProvider>
        ) : (
          <FullPageError detail={message} languagePair={languagePair} statusCode={statusCode} />
        )}
      </TrackedPage>
    </LanguagePairProvider>
  );
};

MemriseErrorPage.getInitialProps = async (
  context: MemrisePageContext,
): Promise<MemriseErrorPageProps> => {
  const { asPath, err, res } = context;

  const { httpStatusCode: statusCode, message } = convertToMemriseError(
    err || /* istanbul ignore next: untested branch of code, please test */ null,
    res,
  );

  if (statusCode === STATUS_CODE_NOT_FOUND) {
    const path = asPath?.split('?').at(0);

    if (asPath && path && path !== path.toLocaleLowerCase() && res) {
      const location = asPath.replace(path, path.toLocaleLowerCase());
      res.writeHead(301, { location });
      res.end();
      return { message, statusCode };
    }
  }

  const languagePair = getLanguagePairFromPath(asPath);

  const providerProps =
    statusCode === STATUS_CODE_NOT_FOUND
      ? await tryGetProviderServerSideProps(languagePair)
      : undefined;

  return {
    message,
    statusCode,
    languagePair: providerProps?.languagePair,
    targetLanguages: providerProps?.languagePairs,
  };
};

function getLanguagePairFromPath(asPath: string | undefined) {
  if (!asPath || !getIsLocalizedPath(asPath)) return DEFAULT_LANGUAGE_PAIR;

  const [, sourceLanguageLocale, learnSlug, courseSlug] = asPath.split('/');

  return (
    getLanguagePair({
      sourceLanguageLocale,
      learnSlug,
      courseSlug,
    }) || DEFAULT_LANGUAGE_PAIR
  );
}

async function tryGetProviderServerSideProps(
  ...args: Parameters<typeof getProviderServerSideProps>
) {
  try {
    return await getProviderServerSideProps(...args);
  } catch (e) /* istanbul ignore next */ {
    return undefined;
  }
}

export default MemriseErrorPage;
